"use client";

import * as React from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { cn } from "../utils";
import { IconButton } from "../icon-button";
import { cardCarouselVariants } from "./card-carousel.variants";
import type { CardCarouselProps } from "./card-carousel.types";
const TRANSITION = "transform 0.7s cubic-bezier(0.12, 0.81, 0.31, 1.03), opacity 0.7s ease-out";
interface CardCarouselContextValue {
  scrollPrev: () => void;
  scrollNext: () => void;
  selectedIndex: number;
  slidesCount: number;
}
const CardCarouselContext = React.createContext<CardCarouselContextValue | null>(null);
export function useCardCarousel() {
  return React.useContext(CardCarouselContext);
}
const CardCarousel = React.forwardRef<HTMLDivElement, CardCarouselProps>(({
  children,
  autoplay = false,
  autoplayInterval = 5000,
  loop = true,
  showNavigation = true,
  showPagination = true,
  navigationVariant = "grey",
  navigationSize = "sm",
  controlsClassName,
  className,
  "aria-label": ariaLabel = "Card carousel",
  ...props
}, ref) => {
  const plugins = React.useMemo(() => {
    if (!autoplay) return [];
    return [Autoplay({
      delay: autoplayInterval,
      stopOnInteraction: false,
      stopOnMouseEnter: true
    })];
  }, [autoplay, autoplayInterval]);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop,
    watchDrag: false
  }, plugins);
  const slides = React.Children.toArray(children);
  const slideRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const selectedRef = React.useRef(0);
  const cleanupTimer = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const onSelect = React.useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();
    const oldIndex = selectedRef.current;
    if (newIndex === oldIndex) return;
    selectedRef.current = newIndex;
    setSelectedIndex(newIndex);
    const newSlide = slideRefs.current[newIndex];
    const oldSlide = slideRefs.current[oldIndex];

    // 1. Position incoming slide off-screen right, no transition
    if (newSlide) {
      newSlide.style.transition = "none";
      newSlide.style.transform = "translate3d(100%, 0, 0) scale(1)";
      newSlide.style.opacity = "1";
      newSlide.style.zIndex = "3";
      newSlide.style.pointerEvents = "auto";
      // Force reflow so browser registers the starting position
      newSlide.getBoundingClientRect();
    }

    // 2. Animate incoming slide from right to center
    if (newSlide) {
      newSlide.style.transition = TRANSITION;
      newSlide.style.transform = "translate3d(0, 0, 0) scale(1)";
    }

    // 3. Animate outgoing slide: scale down + push back in Z-space
    if (oldSlide) {
      oldSlide.style.transition = TRANSITION;
      oldSlide.style.transform = "translate3d(0, 0, -400px) scale(0.85)";
      oldSlide.style.opacity = "0";
      oldSlide.style.zIndex = "1";
      oldSlide.style.pointerEvents = "none";
    }

    // 4. After transition ends, park old slide off-screen and reset z-indices
    if (cleanupTimer.current) clearTimeout(cleanupTimer.current);
    cleanupTimer.current = setTimeout(() => {
      if (oldSlide && selectedRef.current !== oldIndex) {
        oldSlide.style.transition = "none";
        oldSlide.style.transform = "translate3d(100%, 0, 0) scale(1)";
        oldSlide.style.opacity = "0";
        oldSlide.style.zIndex = "0";
      }
      if (newSlide) {
        newSlide.style.zIndex = "2";
      }
    }, 750);
  }, [emblaApi]);
  React.useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
      if (cleanupTimer.current) clearTimeout(cleanupTimer.current);
    };
  }, [emblaApi, onSelect]);
  const scrollPrev = React.useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);
  const scrollNext = React.useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);
  const hasMultipleSlides = slides.length > 1;
  const contextValue = React.useMemo<CardCarouselContextValue>(() => ({
    scrollPrev,
    scrollNext,
    selectedIndex,
    slidesCount: slides.length
  }), [scrollPrev, scrollNext, selectedIndex, slides.length]);
  return <CardCarouselContext.Provider value={contextValue}>
        <div ref={ref} className={cn(cardCarouselVariants({
      className
    }))} role="region" aria-label={ariaLabel} aria-roledescription="carousel" data-testid="card-carousel" {...props}>
          {/* Hidden Embla container — drives autoplay, loop, snap state */}
          <div ref={emblaRef} aria-hidden="true" className="pointer-events-none absolute inset-x-0 h-0 overflow-hidden">
            <div className="flex">
              {slides.map((_, index) => <div key={index} className="min-w-0 flex-[0_0_100%]" />)}
            </div>
          </div>

          {/* Visual slides — stacked via CSS grid, animated imperatively */}
          <div className="overflow-hidden [perspective:1200px]">
            <div className="grid items-end">
              {slides.map((slide, index_0) => <div key={index_0} ref={el => {
            slideRefs.current[index_0] = el;
          }} className="[grid-area:1/1]" style={{
            transform: index_0 === 0 ? "translate3d(0, 0, 0) scale(1)" : "translate3d(100%, 0, 0) scale(1)",
            opacity: index_0 === 0 ? 1 : 0,
            zIndex: index_0 === 0 ? 2 : 0,
            pointerEvents: index_0 === 0 ? "auto" : "none"
          }} role="group" aria-roledescription="slide" aria-label={`Slide ${index_0 + 1} of ${slides.length}`}>
                  {slide}
                </div>)}
            </div>
          </div>

          {hasMultipleSlides && (showNavigation || showPagination) && <div className={cn("mt-2 flex items-center gap-2", controlsClassName)}>
              {showNavigation && <IconButton icon="chevron-left" variant={navigationVariant} size={navigationSize} aria-label="Previous slide" onClick={scrollPrev} data-testid="card-carousel-prev" />}
              {showPagination && <span className="typography-body-sm-medium text-text-primary select-none" data-testid="card-carousel-pagination">
                  {selectedIndex + 1}/{slides.length}
                </span>}
              {showNavigation && <IconButton icon="chevron-right" variant={navigationVariant} size={navigationSize} aria-label="Next slide" onClick={scrollNext} data-testid="card-carousel-next" />}
            </div>}
        </div>
      </CardCarouselContext.Provider>;
});
CardCarousel.displayName = "CardCarousel";
export { CardCarousel };